import * as zod from 'zod';

import type {
  DeviceCommand,
  DeviceConfiguration,
  DeviceKind,
  EquipmentItem,
} from '@sb/integrations/types';
import {
  buildForceSensorCommand,
  FORCE_SENSOR_KINDS,
  isForceSensor,
  isForceSensorKind,
} from '@sb/integrations/utils';
import { ActionRequiredError, Step } from '@sb/remote-control/types';

export namespace ZeroForceTorqueSensorStepDatabase {
  export const name = 'Zero Force Torque Sensor';
  export const description = 'Zero the enabled force/torque sensor';
  // Only surfaced in the step library when a force/torque sensor is enabled.
  export const deviceKinds = FORCE_SENSOR_KINDS;
  export const librarySection = Step.LibrarySection.InterfaceWithMachines;
  export const librarySort = '2';
  export const argumentKind = 'ZeroForceTorqueSensor';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    // The equipment ID of the selected sensor.
    deviceID: zod.string().optional(),
    // deviceKind is preserved alongside deviceID so cross-robot imports can
    // remap to a same-kind sensor on the target robot.
    deviceKind: zod.string().optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  // eslint-disable-next-line no-inner-declarations
  function resolveForceSensor(
    equipment: EquipmentItem[],
    configuration: Arguments,
  ): DeviceConfiguration {
    let sensor: DeviceConfiguration | undefined;

    if (configuration.deviceID) {
      const selectedDevice = equipment.find(
        (item) => item.id === configuration.deviceID,
      );

      if (selectedDevice && isForceSensor(selectedDevice.config.kind)) {
        sensor = selectedDevice.config;
      } else if (selectedDevice) {
        throw new ActionRequiredError({
          kind: 'missingEquipment',
          message: `Selected device "${selectedDevice.config.name || selectedDevice.id}" is not a force/torque sensor. Please select a force/torque sensor in the Equipment Manager.`,
        });
      }
    }

    // Fall back to the enabled force/torque sensor when no selection was made
    // or the selected device is no longer present.
    if (!sensor) {
      sensor = equipment
        .map((item) => item.config)
        .find((config) => isForceSensor(config.kind));
    }

    if (!sensor) {
      const error = new Error(
        'Enable a force/torque sensor to use the Zero Force Torque sensor step',
      );

      error.name = 'EquipmentNotFound';
      throw error;
    }

    return sensor;
  }

  // eslint-disable-next-line no-inner-declarations
  export function buildZeroCommand(kind: DeviceKind): DeviceCommand {
    if (!isForceSensorKind(kind)) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Force/torque sensor ${kind} is not supported for this step`,
      });
    }

    return buildForceSensorCommand(kind, 'zero');
  }

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    equipment,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const sensor = resolveForceSensor(equipment, args);

    return {
      ...stepData,
      stepKind: 'ZeroForceTorqueSensor',
      args: {
        command: buildZeroCommand(sensor.kind),
        deviceID: args.deviceID,
      },
    };
  };
}

ZeroForceTorqueSensorStepDatabase satisfies Step.StepKindInfo;
